import { debug, error } from '@/utils/logger';

const API_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL;
const BOOKING_CLICK_URL = `${API_URL}/api/v1/public/events/booking-click`;
const MONGO_OBJECT_ID = /^[0-9a-fA-F]{24}$/;

export type BookingClickApiSurface = 'place_modal' | 'list_timeline' | 'itinerary_timeline';

/**
 * Mixpanel and campaign refs use `*_card` suffixes. The public API enum does not.
 */
export function toBookingClickSourceSurface(
  surface: string,
): BookingClickApiSurface | undefined {
  if (surface === 'place_modal') return 'place_modal';
  if (surface === 'list_timeline' || surface === 'list_timeline_card') return 'list_timeline';
  if (surface === 'itinerary_timeline' || surface === 'itinerary_timeline_card') {
    return 'itinerary_timeline';
  }
  return undefined;
}

function mongoId(value: string | null | undefined): string | undefined {
  if (!value || !MONGO_OBJECT_ID.test(value)) return undefined;
  return value;
}

function optionalAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (typeof window === 'undefined') return headers;

  const rawAccessToken = localStorage.getItem('accessToken');
  if (!rawAccessToken) return headers;

  headers.Authorization = rawAccessToken.startsWith('Bearer ')
    ? rawAccessToken.substring(7)
    : rawAccessToken;

  const rawIdToken = localStorage.getItem('idToken');
  if (rawIdToken) {
    headers['x-id-token'] = rawIdToken.startsWith('Bearer ')
      ? rawIdToken.substring(7)
      : rawIdToken;
  }

  return headers;
}

export interface RecordPublicBookingClickParams {
  locationId?: string | null;
  postId?: string | null;
  referralCreatorId?: string | null;
  postLocationId?: string | null;
  /** Mixpanel or campaign surface. Unmapped surfaces are not sent. */
  sourceSurface: string;
}

/**
 * Fire-and-forget public booking click. Never throws.
 * Skips when the API would reject: no credited creator (referrer or post), no Mongo location, or an unsupported surface.
 */
export async function recordPublicBookingClick(
  params: RecordPublicBookingClickParams,
): Promise<void> {
  const sourceSurface = toBookingClickSourceSurface(params.sourceSurface);
  const locationId = mongoId(params.locationId);
  const postId = mongoId(params.postId);
  const referralCreatorId = mongoId(params.referralCreatorId);
  const postLocationId = mongoId(params.postLocationId);

  if (!sourceSurface || !locationId || (!referralCreatorId && !postId)) {
    return;
  }

  const body: Record<string, string> = {
    location_id: locationId,
    source_surface: sourceSurface,
  };
  if (postId) body.post_id = postId;
  if (referralCreatorId) body.referral_creator_id = referralCreatorId;
  if (postLocationId) body.post_location_id = postLocationId;

  try {
    debug('[bookingClicks] Recording booking click', body);
    const res = await fetch(BOOKING_CLICK_URL, {
      method: 'POST',
      headers: optionalAuthHeaders(),
      body: JSON.stringify(body),
    });
    if (res.status === 429) {
      debug('[bookingClicks] Booking click rate limited', { locationId });
      return;
    }
    if (!res.ok) {
      error('[bookingClicks] Booking click rejected', { status: res.status, locationId });
    }
  } catch (err) {
    error('[bookingClicks] Failed to record booking click', err);
  }
}
